// Pure helpers for member views (no network, no storage) – covered by offline tests.
export const text = (v) => (typeof v === "string" ? v : v ? v.de ?? v.en ?? "" : "");

// Reserved test domains only (RFC 2606 / 6761): example.com|org|net, .example, .invalid, .test.
const TEST_MAIL = /^[^@\s]+@([a-z0-9-]+\.)*(example\.com|example\.org|example\.net|example|invalid|test)$/i;
export const isTestAddress = (email) => typeof email === "string" && TEST_MAIL.test(email.trim());

const norm = (v) =>
  String(v ?? "").toLocaleLowerCase("de").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

export const STATUS = {
  none: "Nicht eingetragen",
  registered: "Teilnahme eingetragen",
  requested: "Anfrage ausstehend",
  waitlisted: "Auf der Warteliste",
  offered: "Platz angeboten",
  confirmed: "Teilnahme bestätigt",
  declined: "Abgelehnt",
  withdrawn: "Zurückgezogen",
  cancelled: "Von dir abgesagt",
  activity_cancelled: "Aktivität abgesagt",
  saved: "Gemerkt",
  incoming: "Anfrage erhalten",
  outgoing: "Anfrage gesendet – ausstehend",
  accepted: "Kontakt bestätigt",
  member: "Mitglied",
  not_member: "Kein Mitglied",
  join_pending: "Beitritt angefragt – nicht bestätigt",
  join_declined: "Beitrittsanfrage abgelehnt",
  unknown: "Status noch unbekannt",
};

// Own-property reads only, so prototype keys or unexpected data are never taken as a status.
export const hasOwn = (o, k) => o !== null && typeof o === "object" && Object.prototype.hasOwnProperty.call(o, k);
export const own = (o, k) => (hasOwn(o, k) ? o[k] : undefined);
export function lookup(map, v) {
  if (typeof v !== "string") return undefined;
  const k = v.trim().toLocaleLowerCase("de").normalize("NFC");
  return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : undefined;
}

// Core v2.10: participation = null | angefragt | bestätigt | warteliste | angeboten | abgelehnt | storniert; English values from older shapes.
// "angeboten" reserves nothing: it is never confirmed and, like every state except "bestätigt", contradicts joined: true.
const PARTICIPATION = { angefragt: "requested", "bestätigt": "confirmed", bestaetigt: "confirmed", abgelehnt: "declined", warteliste: "waitlisted", angeboten: "offered", storniert: "cancelled",
  requested: "requested", confirmed: "confirmed", declined: "declined" };
const LEGACY_STATE = { registered: "registered", requested: "requested", confirmed: "confirmed", declined: "declined", withdrawn: "withdrawn", cancelled: "cancelled" };
const CANCELLED = { abgesagt: true, cancelled: true };

export const isActivityCancelled = (a) => lookup(CANCELLED, own(a, "status")) === true;

export function contactGroups(conversations) {
  const direct = (conversations || []).filter((c) => c.type === "direct");
  return {
    incoming: direct.filter((c) => c.status === "pending" && !c.is_requester),
    outgoing: direct.filter((c) => c.status === "pending" && c.is_requester),
    accepted: direct.filter((c) => c.status === "accepted"),
  };
}

export function contactStateFor(conversations, memberId) {
  const conv = (conversations || []).find((c) => c.type === "direct" && c.other && c.other.id === memberId);
  if (!conv) return { state: "none", conv: null };
  if (conv.status === "accepted") return { state: "accepted", conv };
  if (conv.status === "pending") return { state: conv.is_requester ? "outgoing" : "incoming", conv };
  return { state: conv.status, conv };
}

// A cancelled activity wins over any earlier commitment; joined is true only with "bestätigt" (v2.6), anything else contradicts.
export function participationState(activity) {
  if (!activity || typeof activity !== "object") return "none";
  if (isActivityCancelled(activity)) return "activity_cancelled";
  const joined = own(activity, "joined") === true;
  const raw = own(activity, "participation");
  if (raw !== undefined && raw !== null) {
    const p = lookup(PARTICIPATION, raw);
    if (!p) return "unknown";
    if (p === "confirmed") return joined ? "confirmed" : "unknown";
    return joined ? "unknown" : p;
  }
  const legacy = lookup(LEGACY_STATE, own(own(activity, "my_participation"), "state"));
  if (legacy) return legacy;
  if (!joined) return "none";
  return own(activity, "requires_confirmation") === true ? "unknown" : "registered";
}

const PLAN_STATES = ["registered", "confirmed", "requested", "waitlisted", "offered", "declined", "unknown", "activity_cancelled"];
const takesPart = (a) => own(a, "joined") === true || ((own(a, "participation") ?? null) !== null && lookup(PARTICIPATION, own(a, "participation")) !== "cancelled") || hasOwn(a, "my_participation");

export function planEntries(activities, now = Date.now()) {
  const mine = (activities || []).filter((a) => {
    const s = participationState(a);
    return PLAN_STATES.includes(s) && (!["activity_cancelled", "unknown"].includes(s) || takesPart(a));
  });
  const sorted = [...mine].sort((a, b) => new Date(a.date) - new Date(b.date));
  const isPast = (a) => a.date && new Date(a.date).getTime() < now;
  return { upcoming: sorted.filter((a) => !isPast(a)), past: sorted.filter(isPast).reverse() };
}

export function spotsLeft(activity) {
  const cap = Number(activity && activity.capacity);
  const taken = Number(activity && activity.participants_count);
  if (!Number.isFinite(cap) || cap <= 0 || !Number.isFinite(taken)) return null;
  return Math.max(0, cap - taken);
}

export const ACCESS_FEATURES = [
  ["step_free", "Stufenloser Eingang"],
  ["quiet_area", "Ruhiger Bereich"],
  ["toilet", "Geeignetes WC"],
  ["hearing_loop", "Höranlage"],
  ["assistance_dog_info", "Information zu Assistenzhunden"],
];

const ACCESS_STATE = { present: "Angabe vorhanden", absent: "Nicht vorhanden", unknown: "Unbekannt – noch keine Angabe" };
// Core activity.access.features keys; "wc" means a suitable toilet, not a certified accessible one. No Core key for assistance dogs.
const CORE_ACCESS_KEY = { step_free: "stufenlos", quiet_area: "ruhe", toilet: "wc", hearing_loop: "hoeranlage" };
const CORE_ACCESS_VALUE = { ja: "present", nein: "absent", unbekannt: "unknown" };
const LEGACY_ACCESS_VALUE = { present: "present", absent: "absent", unknown: "unknown" };
const exact = (map, v) => (typeof v === "string" && Object.prototype.hasOwnProperty.call(map, v) ? map[v] : undefined);
const isFeatureObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:\d{2}))?$/;

// Calendar-exact ISO date (2026-02-30 is rejected, although Date.parse rolls it over).
export function validIsoDate(v) {
  const m = typeof v === "string" ? ISO_DATE.exec(v) : null;
  if (!m || Number.isNaN(Date.parse(v))) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const day = new Date(Date.UTC(y, mo - 1, d));
  return day.getUTCFullYear() === y && day.getUTCMonth() === mo - 1 && day.getUTCDate() === d;
}

// Core accepts a source of up to 120 characters (Python code points, routes_control_activities.py), so count code points, not UTF-16 units.
const sourceText = (v) => (typeof v === "string" && v.trim() && Array.from(v).length <= 120 && !/[\u0000-\u001f\u007f]/.test(v) ? v.trim() : null);

// One feature: a real Core entry (own key in access.features) wins over legacy access_features[]; anything malformed is unknown.
function readFeature(activity, key) {
  const features = own(own(activity, "access"), "features");
  const coreKey = exact(CORE_ACCESS_KEY, key);
  if (coreKey && isFeatureObject(features) && hasOwn(features, coreKey)) {
    const e = features[coreKey];
    return isFeatureObject(e) ? { state: exact(CORE_ACCESS_VALUE, own(e, "value")) || "unknown", source: own(e, "source"), updatedAt: own(e, "updated_at") } : { state: "unknown" };
  }
  const legacy = own(activity, "access_features");
  const f = Array.isArray(legacy) ? legacy.find((x) => own(x, "key") === key) : undefined;
  return f ? { state: exact(LEGACY_ACCESS_VALUE, own(f, "state")) || "unknown", source: own(f, "source"), updatedAt: own(f, "updated_at") } : { state: "unknown" };
}

export function accessFeatures(activity) {
  return ACCESS_FEATURES.map(([key, label]) => {
    const e = readFeature(activity, key);
    const known = e.state !== "unknown";
    return { key, label, state: e.state, stateLabel: ACCESS_STATE[e.state], source: known ? sourceText(e.source) : null, updatedAt: known && validIsoDate(e.updatedAt) ? e.updatedAt : null };
  });
}

// Optional free-text directions (access.directions): shown separately, never read as a confirmed feature.
export function accessDirections(activity) {
  const v = own(own(activity, "access"), "directions");
  return typeof v === "string" ? v.trim() : "";
}

export function searchAll({ activities = [], groups = [], members = [] }, query) {
  const q = norm(query);
  if (q.length < 2) return { activities: [], groups: [], members: [] };
  const hit = (...vals) => vals.some((v) => norm(v).includes(q));
  return {
    activities: activities.filter((a) => hit(text(a.title), text(a.description), a.location)),
    groups: groups.filter((g) => hit(text(g.name), text(g.description))),
    members: members.filter((m) => hit(m.name, m.city, ...(m.interests || []))),
  };
}

export function filterMembers(members, { city = "", interest = "", language = "" } = {}) {
  return (members || []).filter((m) =>
    (!city || norm(m.city) === norm(city)) &&
    (!interest || (m.interests || []).includes(interest)) &&
    (!language || (m.languages || []).map(norm).includes(norm(language))));
}

export function distinctValues(items, pick) {
  const out = new Map();
  (items || []).forEach((i) => [].concat(pick(i) || []).forEach((v) => {
    if (v && !out.has(norm(v))) out.set(norm(v), v);
  }));
  return [...out.values()].sort((a, b) => String(a).localeCompare(String(b), "de"));
}

// M59: with chosen features, keep activities where each feature is present; unknown ones only on request.
export function filterByAccess(activities, keys = [], includeUnknown = false) {
  if (!keys.length) return activities || [];
  return (activities || []).filter((a) => {
    const f = accessFeatures(a);
    return keys.every((k) => {
      const x = f.find((y) => y.key === k);
      return x && (x.state === "present" || (includeUnknown && x.state === "unknown"));
    });
  });
}
